import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/ui/form';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/shared/ui/select';
import { DatePicker } from '@/shared/ui/date-picker';
import { MonthPicker } from '@/shared/widgets/MonthPicker';
import { startOfMonth } from 'date-fns';
import { useToast } from '@/shared/hooks/use-toast';
import {
  useCreateReceivable,
  useUpdateReceivable,
  useReceivable,
  useCreateReceivableDocument,
  useCreateReceivableTax,
  useCreateReceivableTaxDocument,
} from '../hooks';
import { useReceivableShareConfigs } from '@/features/receivable-share-configs/hooks';
import {
  ReceivableCurrency,
  ReceivableStatus,
  ReceivableStore,
  PendingReceivableDocument,
  PendingReceivableTax,
  type CreateReceivablePayload,
} from '../utils/types';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { CurrencyInput } from '@/shared/ui/currency-input';
import { ReceivableDocumentsSection, ReceivableTaxesSection, ReceivableStatusBadge } from '../components';
import { Alert, AlertDescription, AlertTitle } from '@/shared/ui/alert';
import { receivableFormSchema, type ReceivableFormValues } from '../model/receivable.schema';
import { FormFooter } from '@/shared/widgets';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';

// Converte data para o primeiro dia do mês no formato YYYY-MM-DD (para relatedDate)
// Extrai apenas ano e mês da data local, sem considerar timezone
const toMonthIso = (date?: Date) => {
  if (!date) return undefined;
  const monthStart = startOfMonth(date);
  // Usa os métodos locais para extrair ano, mês e dia
  const year = monthStart.getFullYear();
  const month = String(monthStart.getMonth() + 1).padStart(2, '0');
  const day = String(monthStart.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Formata data exata para YYYY-MM-DD (para paidDate)
// Extrai apenas ano, mês e dia da data local, sem considerar timezone
const formatDateForApi = (date?: Date) => {
  if (!date) return undefined;
  try {
    // Usa os métodos locais para extrair ano, mês e dia
    // Isso garante que a data selecionada pelo usuário seja preservada
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch {
    return undefined;
  }
};

// Converte string do backend (YYYY-MM-DD ou ISO string) para Date, evitando problemas de timezone
// Cria a data no timezone local para que o DatePicker exiba corretamente
const parseDateFromBackend = (dateString?: string): Date | undefined => {
  if (!dateString) return undefined;

  // Extrai a parte da data (YYYY-MM-DD) de uma ISO string ou usa a string diretamente
  let datePart: string;
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    // Já está no formato YYYY-MM-DD
    datePart = dateString;
  } else if (/^\d{4}-\d{2}-\d{2}T/.test(dateString)) {
    // É uma ISO string, extrai apenas a parte da data
    datePart = dateString.split('T')[0];
  } else {
    // Formato desconhecido, tenta usar new Date normalmente
    return new Date(dateString);
  }

  // Cria a data no timezone local (não UTC) para que o DatePicker exiba a data correta
  // Isso garante que se o backend retornou "2025-04-01", o DatePicker mostrará 01/04/2025
  const [year, month, day] = datePart.split('-').map(Number);
  return new Date(year, month - 1, day);
};

type FormData = ReceivableFormValues;

export function ReceivableForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { toast } = useToast();

  const { receivable, isLoading: loadingReceivable } = useReceivable(id, { enabled: isEdit });
  const { createReceivable, isLoading: creating } = useCreateReceivable();
  const { updateReceivable, isLoading: updating } = useUpdateReceivable();
  const { createDocument: uploadReceivableDocument } = useCreateReceivableDocument();
  const { createTax: uploadReceivableTax } = useCreateReceivableTax();
  const { createTaxDocument: uploadReceivableTaxDocument } = useCreateReceivableTaxDocument();
  const [pendingDocuments, setPendingDocuments] = useState<PendingReceivableDocument[]>([]);
  const [uploadingPendingDocs, setUploadingPendingDocs] = useState(false);
  const [pendingTaxes, setPendingTaxes] = useState<PendingReceivableTax[]>([]);
  const [uploadingPendingTaxes, setUploadingPendingTaxes] = useState(false);
  const [showConfirmConcludedDialog, setShowConfirmConcludedDialog] = useState(false);

  const { shareConfigs, isLoading: loadingShareConfigs } = useReceivableShareConfigs({ page: 1, limit: 100 });
  const shareConfigOptions = useMemo(
    () => shareConfigs.map((config) => ({ value: config.id, label: config.name })),
    [shareConfigs],
  );

  const form = useForm<FormData>({
    resolver: zodResolver(receivableFormSchema),
    defaultValues: {
      currency: ReceivableCurrency.BRL,
      status: ReceivableStatus.WAITING,
      store: ReceivableStore.APPLE,
      grossValue: 0,
      relatedDate: undefined,
      paidDate: undefined,
      receivableShareConfigId: undefined,
    },
  });
  const currentStatus = form.watch('status');
  const isStatusConcluded = currentStatus === ReceivableStatus.CONCLUDED;
  const canMarkAsConcluded = isEdit && currentStatus=== ReceivableStatus.WAITING

  const handleMarkAsConcluded = () => {
    if (!id || !canMarkAsConcluded) return;
    setShowConfirmConcludedDialog(true);
  };

  const handleConfirmMarkAsConcluded = async () => {
    if (!id || !canMarkAsConcluded) return;

    setShowConfirmConcludedDialog(false);

    try {
      const formData = form.getValues();
      const relatedDateIso = toMonthIso(formData.relatedDate);
      const paidDateIso = formatDateForApi(formData.paidDate);

      const payload: CreateReceivablePayload = {
        currency: formData.currency,
        status: ReceivableStatus.CONCLUDED,
        store: formData.store,
        grossValue: formData.grossValue,
        relatedDate: relatedDateIso!,
        receivableShareConfigId: formData.receivableShareConfigId,
      };

      if (paidDateIso) payload.paidDate = paidDateIso;

      await updateReceivable(id, payload);
      form.setValue('status', ReceivableStatus.CONCLUDED);
      toast({
        title: 'Recebível marcado como concluído',
        description: 'O cálculo será processado em breve.',
      });
      navigate('/receivables');
    } catch (error) {
      toast({
        title: 'Erro ao marcar como concluído',
        description: error instanceof Error ? error.message : 'Ocorreu um erro inesperado.',
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    if (receivable) {
      const resolvedShareConfigId =
        receivable.receivableShareConfig?.id ?? receivable.receivableShareConfigId;
      form.reset({
        currency: receivable.currency,
        status: receivable.status,
        store: receivable.store,
        grossValue: receivable.grossValue,
        relatedDate: parseDateFromBackend(receivable.relatedDate),
        paidDate: parseDateFromBackend(receivable.paidDate),
        receivableShareConfigId: resolvedShareConfigId ?? undefined,
      });
    }
  }, [receivable, form]);

  const handlePendingDocumentCreate = (document: PendingReceivableDocument) => {
    setPendingDocuments((prev) => [...prev, document]);
  };

  const handlePendingDocumentUpdate = (document: PendingReceivableDocument) => {
    setPendingDocuments((prev) => prev.map((item) => (item.id === document.id ? document : item)));
  };

  const handlePendingDocumentDelete = (id: string) => {
    setPendingDocuments((prev) => prev.filter((item) => item.id !== id));
  };

  const handlePendingTaxCreate = (tax: PendingReceivableTax) => {
    setPendingTaxes((prev) => [...prev, tax]);
  };

  const handlePendingTaxUpdate = (tax: PendingReceivableTax) => {
    setPendingTaxes((prev) => prev.map((item) => (item.id === tax.id ? tax : item)));
  };

  const handlePendingTaxDelete = (id: string) => {
    setPendingTaxes((prev) => prev.filter((item) => item.id !== id));
  };

  const isSubmitting = creating || updating || uploadingPendingDocs || uploadingPendingTaxes;

  const onSubmit = async (data: FormData) => {
    const relatedDateIso = toMonthIso(data.relatedDate);
    const paidDateIso = formatDateForApi(data.paidDate);

    if (!relatedDateIso) {
      form.setError('relatedDate', {
        type: 'manual',
        message: 'Informe a data de referência',
      });
      return;
    }

    const payload: CreateReceivablePayload = {
      currency: data.currency,
      status: data.status,
      store: data.store,
      grossValue: data.grossValue,
      relatedDate: relatedDateIso,
      receivableShareConfigId: data.receivableShareConfigId,
    };

    if (paidDateIso) payload.paidDate = paidDateIso;


    try {
      if (isEdit && id) {
        await updateReceivable(id, payload);
        navigate('/receivables');
        return;
      }

      const created = await createReceivable(payload);
      if (!created?.id) {
        toast({
          title: 'Erro ao criar recebível',
          description: 'ID não retornado pela API. Tente novamente.',
          variant: 'destructive',
        });
        return;
      }

      if (pendingDocuments.length > 0) {
        setUploadingPendingDocs(true);
        let uploadedCount = 0;
        try {
          for (const document of pendingDocuments) {
            await uploadReceivableDocument({
              receivableId: created.id,
              documentType: document.documentType,
              description: document.description,
              documentUrl: document.documentUrl,
              file: document.file,
            });
            uploadedCount += 1;
          }
          setPendingDocuments([]);
        } catch (error) {
          if (uploadedCount > 0) {
            setPendingDocuments((current) => current.slice(uploadedCount));
          }
          toast({
            title: 'Alguns documentos não foram enviados',
            description: 'Você poderá anexá-los novamente na edição do recebível.',
            variant: 'destructive',
          });
        } finally {
          setUploadingPendingDocs(false);
        }
      }

      if (pendingTaxes.length > 0) {
        setUploadingPendingTaxes(true);
        let uploadedTaxes = 0;
        try {
          for (const tax of pendingTaxes) {
            const createdTax = await uploadReceivableTax({
              receivableId: created.id,
              currency: tax.currency,
              type: tax.type,
              description: tax.description,
              document: tax.document,
              value: tax.value,
            });
            if (tax.documents && tax.documents.length > 0) {
              for (const document of tax.documents) {
                try {
                  await uploadReceivableTaxDocument({
                    receivableTaxId: createdTax.id,
                    documentType: document.documentType,
                    description: document.description,
                    documentUrl: document.documentUrl,
                    file: document.file,
                  });
                } catch (error) {
                  console.error('Erro ao anexar documento da taxa:', error);
                }
              }
            }
            uploadedTaxes += 1;
          }
          setPendingTaxes([]);
        } catch (error) {
          if (uploadedTaxes > 0) {
            setPendingTaxes((current) => current.slice(uploadedTaxes));
          }
          toast({
            title: 'Algumas taxas não foram enviadas',
            description: 'Você poderá cadastrá-las novamente na edição do recebível.',
            variant: 'destructive',
          });
        } finally {
          setUploadingPendingTaxes(false);
        }
      }

      navigate('/receivables');
    } catch (error) {
      console.error('Erro ao salvar recebível:', error);
    }
  };

  if (isEdit && loadingReceivable) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/receivables')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Carregando...</h1>
            <p className="text-muted-foreground">Buscando dados do recebível</p>
          </div>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 ">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/receivables')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {isEdit ? 'Editar Recebível' : 'Novo Recebível'}
          </h1>
          <p className="text-muted-foreground">
            {isEdit ? 'Atualize as informações do recebível' : 'Preencha os dados para criar um novo recebível'}
          </p>
        </div>
      </div>

      <Card className='bg-white/[0.03] border-white/[0.06]'>
        <CardHeader>
          <CardTitle>Informações do Recebível</CardTitle>
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {/* Status do Recebível */}
              <div className="space-y-4 rounded-lg border border-border bg-card p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <h3 className="text-base font-semibold text-foreground">Status do Recebível</h3>
                      <ReceivableStatusBadge status={currentStatus} />
                    </div>
                    {currentStatus === ReceivableStatus.WAITING && (
                      <div className="space-y-3">
                        <p className="text-sm text-muted-foreground">
                          O recebível está aguardando processamento. Você pode editar as informações e anexar documentos.
                        </p>
                        <Alert className="border-amber-500/30 bg-amber-500/5">
                          <AlertDescription className="text-sm font-medium text-foreground">
                            <strong className="text-amber-600 dark:text-amber-400">Importante:</strong> Ao marcar como concluído, o sistema iniciará o cálculo automático dos valores.
                          </AlertDescription>
                        </Alert>
                      </div>
                    )}
                    {currentStatus === ReceivableStatus.RECEIVED && (
                      <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">
                          O recebível foi recebido e está pronto para processamento.
                        </p>
                        <p className="text-xs text-muted-foreground/80">
                          O cálculo será iniciado automaticamente em breve.
                        </p>
                      </div>
                    )}
                    {currentStatus === ReceivableStatus.CALCULATING && (
                      <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">
                          O recebível está sendo processado. Os valores estão sendo calculados automaticamente.
                        </p>
                        <p className="text-xs text-muted-foreground/80">
                          Esta operação pode levar alguns minutos. Não feche esta página.
                        </p>
                      </div>
                    )}
                    {currentStatus === ReceivableStatus.CALCULATED && (
                      <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">
                          O recebível foi processado com sucesso. Todos os cálculos foram concluídos.
                        </p>
                        <p className="text-xs text-muted-foreground/80">
                          Os valores finais estão disponíveis e o recebível está finalizado.
                        </p>
                      </div>
                    )}
                    {currentStatus === ReceivableStatus.CONCLUDED && (
                      <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">
                          O recebível foi marcado como concluído e está aguardando processamento.
                        </p>
                        <p className="text-xs text-muted-foreground/80">
                          O sistema iniciará o cálculo automático em breve.
                        </p>
                      </div>
                    )}
                    {currentStatus === ReceivableStatus.FAIL && (
                      <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">
                          Ocorreu um erro durante o processamento do recebível.
                        </p>
                        <p className="text-xs text-muted-foreground/80">
                          Verifique os dados e tente marcar como concluído novamente.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                {/* Moeda */}
                <FormField
                  control={form.control}
                  name="currency"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Moeda</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione a moeda" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value={ReceivableCurrency.BRL}>BRL</SelectItem>
                          <SelectItem value={ReceivableCurrency.USD}>USD</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormDescription>Moeda do recebível</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {/* Loja */}
                <FormField
                  control={form.control}
                  name="store"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Loja</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione a loja" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value={ReceivableStore.APPLE}>Apple</SelectItem>
                          <SelectItem value={ReceivableStore.GOOGLE}>Google</SelectItem>
                          <SelectItem value={ReceivableStore.STRIPE}>Stripe</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormDescription>Origem do recebível</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

              </div>

              <div className="grid gap-6 md:grid-cols-2">
                {/* Configuração de Rateio */}
                <FormField
                  control={form.control}
                  name="receivableShareConfigId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Configuração de rateio *</FormLabel>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={loadingShareConfigs || isSubmitting}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione a configuração" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {loadingShareConfigs ? (
                            <SelectItem value="__loading__" disabled>
                              Carregando configurações...
                            </SelectItem>
                          ) : (
                            shareConfigOptions.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        Define os pesos utilizados para o cálculo do recebível.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {/* Valor Bruto */}
                <FormField
                  control={form.control}
                  name="grossValue"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Valor Bruto</FormLabel>
                      <FormControl>
                        <CurrencyInput
                          currency={form.watch('currency')}
                          value={field.value}
                          onValueChange={(num) => field.onChange(num ?? 0)}
                          placeholder="R$ 0,00"
                        />
                      </FormControl>
                      <FormDescription>Valor bruto do recebível</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid gap-6 md:grid-cols-2">
                {/* Data de Referência */}
                <FormField
                  control={form.control}
                  name="relatedDate"
                  render={({ field }) => (
                    <FormItem className="flex flex-col w-full">
                      <FormLabel>Data de Referência</FormLabel>
                      <FormControl>
                        <MonthPicker
                          value={field.value ? { month: field.value.getMonth() + 1, year: field.value.getFullYear() } : null}
                          onChange={(val) => field.onChange(val ? new Date(val.year, val.month - 1, 1) : undefined)}
                          placeholder="Selecione mês/ano de referência"
                        />
                      </FormControl>
                      <FormDescription>Selecione o mês/ano base do recebível</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {/* Data de Recebimento */}
                <FormField
                  control={form.control}
                  name="paidDate"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>Recebido em</FormLabel>
                      <DatePicker
                        date={field.value}
                        onSelect={field.onChange}
                        placeholder="Selecione a data de recebimento (opcional)"
                      />
                      <FormDescription>Opcional</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <ReceivableDocumentsSection
                receivableId={isEdit ? id : undefined}
                pendingDocuments={isEdit ? [] : pendingDocuments}
                onPendingDocumentCreate={isEdit ? undefined : handlePendingDocumentCreate}
                onPendingDocumentUpdate={isEdit ? undefined : handlePendingDocumentUpdate}
                onPendingDocumentDelete={isEdit ? undefined : handlePendingDocumentDelete}
                isProcessing={uploadingPendingDocs}
                isLocked={isStatusConcluded}
              />
              <ReceivableTaxesSection
                receivableId={isEdit ? id : undefined}
                pendingTaxes={isEdit ? [] : pendingTaxes}
                onPendingTaxCreate={isEdit ? undefined : handlePendingTaxCreate}
                onPendingTaxUpdate={isEdit ? undefined : handlePendingTaxUpdate}
                onPendingTaxDelete={isEdit ? undefined : handlePendingTaxDelete}
                isProcessing={uploadingPendingTaxes}
              />

              <FormFooter
                isLoading={isSubmitting || updating}
              >
                <div className="flex gap-3 justify-between w-full">
                  <Button type="button" variant="outline" onClick={() => navigate('/receivables')} disabled={isSubmitting || updating}>
                    Cancelar
                  </Button>
                  <div className="flex gap-3">

                    {canMarkAsConcluded && (
                      <Button
                        type="button"
                        onClick={handleMarkAsConcluded}
                        disabled={isSubmitting || updating || !canMarkAsConcluded}
                        className="bg-green-600 hover:bg-green-700 text-white"
                      >
                        {isSubmitting || updating ? 'Processando...' : 'Salvar e Marcar como Concluído'}
                      </Button>
                    )}
                    <Button type="submit" disabled={isSubmitting || updating}>
                      {isSubmitting ? 'Salvando...' : isEdit ? 'Salvar alterações' : 'Criar Recebível'}
                    </Button>
                  </div>
                </div>
              </FormFooter>

            </form>
          </Form>
        </CardContent>
      </Card>

      {/* Diálogo de confirmação para marcar como concluído */}
      <AlertDialog open={showConfirmConcludedDialog} onOpenChange={setShowConfirmConcludedDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Marcar recebível como concluído?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                Esta ação <strong>não pode ser desfeita</strong>. Ao confirmar:
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>O recebível será marcado como concluído</li>
                <li>O sistema iniciará automaticamente o cálculo dos valores</li>
                <li>O processamento pode levar alguns minutos</li>
              </ul>
              <p className="pt-2 font-medium text-foreground">
                Deseja continuar?
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={updating}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmMarkAsConcluded}
              disabled={updating}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {updating ? 'Processando...' : 'Sim, marcar como concluído'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div >
  );
}
